import React, { useEffect, useState, useRef } from 'react';
import { Box, Container, Grid, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChatItem from '../components/ChatItem';
import ChatInput from '../components/ChatInput';
import useWebSocket from '../services/webSocket';
import { useTheme } from '@emotion/react';
import LeftMenu from '../components/LeftMenu';
import ConsentDialog from '../components/ConsentDialog';
import SettingsSection from './SettingsSection';
import SurveySection from './SurveySection';
import LeftMenuMobile from '../components/LeftMenuMobile';
import ErrorDialog from '../components/ErrorDialog';
import { recordEvent } from '../utils/analytics-wrapper';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import remarkGfm from 'remark-gfm';
import remarkExtendedTable, { extendedTableHandlers } from 'remark-extended-table';

export const USER_CONSENT_ATTRIBUTE = 'custom:consent';

const useStyles = () => ({
  messageBox: {
    overflow: 'auto',
    padding: '1rem',
    zIndex: 1,
  },
  messageItem: {
    marginTop: '20px',
  },
  chatContainer: {
    boxShadow: '0px -2px 2px -2px #888',
    paddingLeft: '16px',
    paddingRight: '16px',
    zIndex: 1,
    backgroundColor: 'white',
  },
  chat: {
    zIndex: 0,
  },
  textTitle: {
    fontSize: '30px',
    fontWeight: '500',
    marginBottom: '0px',
  },
  titleAbout: {
    fontSize: '24px',
    fontWeight: '500',
    marginBottom: '0px',
  },
  textSubtitle: {
    fontSize: '15px',
    color: '#00658F',
    marginTop: '0px',
  },
  textSubtitleAbout: {
    fontSize: '12px',
  },
  descriptionText: {
    fontSize: '18px',
  },
  descriptionTextAbout: {
    fontSize: '14px',
  },
  userData: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: '16px',
    marginLeft: 10,
  },
  userSubData: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: '14px',
    marginLeft: 10,
    color: '#41484D',
  },
  linkAbout: {
    color: '#00658F',
    fontSize: '14px',
  },
  changePassword: {
    padding: '10px',
    borderRadius: '20px',
    color: '#00658F',
    borderColor: '#71787E',
    marginRight: 10,
    marginTop: 10,
  },
  signOut: {
    padding: '10px',
    borderRadius: '20px',
    color: '#00658F',
    borderColor: '#71787E',
    marginTop: 10,
  },
  markdown: {
    width: 'max-content',
    wordBreak: 'break-word',
  },
});

const ChatSection = ({
  logger,
  user,
  signOut,
  view,
  setView,
  desktopMode,
  messagesEndRef,
  scrollToBottom,
  beginAutoScroll,
  informedConsentTerms,
  versionText,
  chatTitle,
  chatIntro,
  firstResponseFooter,
  aboutText,
  chatLocation,
  surveyToolsAllowed,
  responseIconUrl,
}) => {
  const theme = useTheme();
  const style = useStyles(theme);
  const messageRef = useRef(null);
  const [salt, setSalt] = useState('');
  const [uuid, setUuid] = useState('');
  const [lastMessage, setLastMessage] = useState(null);
  const [openConsent, setOpenConsent] = useState(false);
  const [numberOfInputRows, setNumberOfInputRows] = useState(1);
  const [cancel, setCancel] = useState(false);

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorAlert, setErrorAlert] = useState(false);

  const [fileName, setFileName] = useState(null);
  const [fileData, setFileData] = useState(null);

  const [connectionClosed, setConnectionClosed] = useState(false);
  const [workingUI, setWorkingUI] = useState(false);
  const working = useRef(false);

  const setWorking = newWorking => {
    // set loading state immediately for those who need the true state of things
    working.current = newWorking;
    // set loading UI in async/batch way to avoid flicker
    setWorkingUI(newWorking);
  }

  const getCurrentDateAndTime = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = (1 + date.getMonth()).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hour = date.getHours().toString().padStart(2, '0');
    const minute = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hour}:${minute}`;
  };

  // initialize chat bubbles with chatIntro as the first message
  const introBubble = {
      position: 'left',
      content: chatIntro,
      sources: '',
      footer: '',
      date: getCurrentDateAndTime(),
      loading: false,
      cancel: false,
      error: false,
      internal: true,
  };
  const [chats, setChats] = useState([introBubble]);

  let currentChat = null;
  const chatsRef = useRef(chats);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  const resetParams = async () => {
    await sessionStorage.removeItem('salt');
    await sessionStorage.removeItem('uuid');
    setSalt('');
    setUuid('');
  };

  const setChatInputFocus = messageRef => {
    setTimeout(() => {
      if (messageRef.current) messageRef.current.focus();
    }, 100);
  };

  const onMessageStart = newMessage => {
    setWorking(true);
    if (newMessage.salt && newMessage.uuid) {
      if (salt !== newMessage.salt) {
        sessionStorage.setItem('salt', newMessage.salt);
        setSalt(newMessage.salt);
      }
      if (uuid !== newMessage.uuid) {
        sessionStorage.setItem('uuid', newMessage.uuid);
        setUuid(newMessage.uuid);
      }
    }
    currentChat = {
      position: 'left',
      content: '',
      sources: '',
      footer: '',
      loading: true,
      error: false,
      internal: false,
      date: getCurrentDateAndTime(),
    };
    beginAutoScroll();
    setChats(oldChats => [...oldChats, currentChat]);
  };

  const onStreamMessage = content => {
    if (currentChat) {
      currentChat = {
        ...currentChat,
        content: currentChat.content + content,
      };
      setChats(oldChats => [...oldChats.slice(0, -1), currentChat]);
    }
  };

  const onMessageEnd = newMessage => {
    setWorking(false);
    setChatInputFocus(messageRef);
    if (currentChat) {
      // if final message is not empty, it contains a Markdown list of sources
      let sourcesCode = '';
      if (newMessage.message.trim() !== '') {
        sourcesCode = (
          <Accordion sx={{ marginBottom: '12px' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="sources-content"
              id="sources-header"
              sx={{ backgroundColor: '#EFF2F5', minHeight: 'auto',
                '& .MuiAccordionSummary-content': { margin: '4px 0px'} }}
            >
              <Typography>Sources consulted</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: '#F9F9FA' }}>
              <Typography>
                <ReactMarkdown
                  style={style.markdown}
                  remarkPlugins={[remarkGfm, extendedTableHandlers, remarkExtendedTable]}
                  breaks={true}
                  linkTarget="_blank"
                >
                  {newMessage.message.trim()}
                </ReactMarkdown>
              </Typography>
            </AccordionDetails>
          </Accordion>
        );
      }

      // if we're the first response in a new chat and we have a first-response footer, prep to display
      const leftPositionChats = chatsRef.current.filter(chat =>
        chat.position === 'left' && !chat.internal);
      let footerCode = '';
      if (leftPositionChats.length <= 1 && firstResponseFooter.trim() !== '') {
        footerCode = (
          <ReactMarkdown
            style={style.markdown}
            remarkPlugins={[remarkGfm, extendedTableHandlers, remarkExtendedTable]}
            breaks={true}
            linkTarget="_blank"
          >
            {'---\n\n' + firstResponseFooter}
          </ReactMarkdown>
        );
      }

      const messageId = newMessage.messageId;
      const finalizedChat = {
        ...currentChat,
        content: currentChat.content,
        sources: sourcesCode,
        footer: footerCode,
        id: messageId,
        loading: false,
      };

      setChats(oldChats => [...oldChats.slice(0, -1), finalizedChat]);
      currentChat = null;
    }
  };

  useEffect(() => {
    const onMessageError = (chats, error) => {
      setWorking(false);
      setChatInputFocus(messageRef);
      let currentChat = chats[chats.length - 1];
      if (currentChat && currentChat.position === 'left') {
        const finalizedChat = {
          ...currentChat,
          content: error,
          loading: false,
          error: true,
        };
        setChats(oldChats => [...oldChats.slice(0, -1), finalizedChat]);
        currentChat = null;
      } else {
        const finalizedChat = {
          position: 'left',
          date: getCurrentDateAndTime(),
          content: error,
          sources: '',
          footer: '',
          loading: false,
          error: true,
        };
        setChats(oldChats => [...oldChats, finalizedChat]);
        currentChat = null;
      }
      setError(false);
      setErrorMessage('');
    };

    if (error) {
      onMessageError(chats, errorMessage);
    }
  }, [chats, error, errorMessage]);

  const onMessageReceived = newMessage => {
    const type = newMessage.type;
    const content = newMessage.message;
    if (type === 'error') {
      setErrorMessage(content);
      setError(true);
    }
    if (type === 'start') {
      onMessageStart(newMessage);
    } else if (type === 'stream') {
      onStreamMessage(content);
    } else if (type === 'end') {
      onMessageEnd(newMessage);
    }
  };

  const onError = async () => {
    // only show errors when working
    if (workingUI) {
      setErrorAlert(true);
      setErrorMessage('Server connection lost. Please check your connection and try again.');
    }
  };

  const onClose = async event => {
    // if we have invalid salt or uuid, reset parameters
    if (event.reason === 'Invalid salt or uuid') {
      await resetParams();
    }

    // if we're loading, handle connection close appropriately
    if (working.current) {
      setConnectionClosed(true);
    }
  };

  const { sendMessage, reconnect } = useWebSocket(onMessageReceived, onError, onClose, null, chatLocation);

  const onMessageSent = (newMessage) => {
    setWorking(true);

    setNumberOfInputRows(1);
    let fullMessage;
    if (fileName && fileData) {
      fullMessage = { message: newMessage, salt, uuid, fileName, surveySheet: JSON.stringify(fileData.rawSurveySheet), choicesSheet: JSON.stringify(fileData.rawChoicesSheet), settingsSheet: JSON.stringify(fileData.rawSettingsSheet) };
    }
    else {
      fullMessage = { message: newMessage, salt, uuid };
    }
    setLastMessage(fullMessage);
    sendMessage(fullMessage);
    showUserMessage(newMessage);
  };

  const showUserMessage = (newMessage, error=false, internal=false) => {
    beginAutoScroll();
    setChats(oldChats => [
      ...oldChats,
      {
        position: 'right',
        content: newMessage,
        sources: '',
        footer: '',
        date: getCurrentDateAndTime(),
        error,
        internal
      },
    ]);
  };

  const showBotMessage = (newMessage, error=false, internal=false) => {
    beginAutoScroll();
    setChats(oldChats => [
      ...oldChats,
      {
        position: 'left',
        content: newMessage,
        sources: '',
        footer: '',
        date: getCurrentDateAndTime(),
        error,
        internal
      },
    ]);
  };

  const useXLSForm = (fileName, fileData, error) => {
    setFileName(fileName);
    setFileData(fileData);
    if (error) {
      showUserMessage(`XLSForm ${fileName} failed to load. ${error}`, true);
    }
    else if (fileName && fileData) {
      showUserMessage(`*${fileName}* attached for context, including ${fileData.surveyItems.length} fields and ${fileData.rawChoicesSheet.length} choice options.`, false, true);
      let botMessage = 'Thank you for attaching your form. Its details will be automatically included with each of your follow-up questions, but be sure to tell me when and how you want me to use it. For example: "Briefly describe my form," or, "In my form, explain how the relevance condition on field xyz works," or, "In my form, how should I do xyz?"';
      const formLength = (JSON.stringify(fileData.rawSurveySheet)+JSON.stringify(fileData.rawChoicesSheet)+JSON.stringify(fileData.rawSettingsSheet)).length;
      if (formLength > 100000) {
        botMessage += '\n\n*Please note: because your form is large, its details might be truncated and I might not be able to see all of it when considering your questions. Apologies in advance if this makes my responses somewhat less sensitive to the details in your form.*';
      }
      showBotMessage(botMessage, false, true);
    }
    // and set focus back to chat input
    setChatInputFocus(messageRef);
  };

  const onResetChat = async () => {
    recordEvent(
      {
        name: 'startNewConversation',
      },
      logger,
    );

    // update date for intro bubble, then reset chat
    introBubble.date = getCurrentDateAndTime();
    setChats([introBubble]);
    setFileName(null);
    setFileData(null);
    await resetParams();
    await reconnect();
    // and set focus back to chat input
    setChatInputFocus(messageRef);
  };

  useEffect(() => {
    scrollToBottom();
  }, [chats, scrollToBottom]);

  useEffect(() => {
    if (user && user.attributes[USER_CONSENT_ATTRIBUTE] !== 'true') {
      setOpenConsent(true);
    }
  }, [user]);

  useEffect(() => {
      const callAsyncReset = async () => {
        await resetParams();
      };

    callAsyncReset();

    setChatInputFocus(messageRef);
  }, []);

  useEffect(() => {
    const onMessageCancel = (salt, uuid, chats) => {
      setWorking(false);
      setChatInputFocus(messageRef);
      let currentChat = chats[chats.length - 1];
      if (currentChat) {
        let message = ' [ Response canceled ]';
        if (currentChat.content === '') {
          message = '[ Request canceled ]';
        }
        const finalizedChat = {
          ...currentChat,
          content: currentChat.content + message,
          loading: false,
          cancel: true,
        };
        setChats(oldChats => [...oldChats.slice(0, -1), finalizedChat]);
        currentChat = null;
      }
      setCancel(false);
      reconnect(salt, uuid);
    };

    if (cancel) {
      onMessageCancel(salt, uuid, chats);
    }
  }, [cancel, salt, reconnect, uuid, chats]);

  useEffect(() => {
    const onConnectionClosed = (chats) => {
      let currentChat = chats[chats.length - 1];
      if (currentChat && (currentChat.position === 'left' || !lastMessage)) {
        // in this case, we're either mid-response or we've already retried, so we'll treat like a cancellation
        setWorking(false);
        setChatInputFocus(messageRef);
        let message = ' [ Connection lost ]';
        if (currentChat.content === '') {
          message = '[ Connection lost ]';
        }
        const finalizedChat = {
          ...currentChat,
          content: currentChat.content + message,
          loading: false,
          cancel: true,
        };
        setChats(oldChats => [...oldChats.slice(0, -1), finalizedChat]);
        currentChat = null;
      }
      else if (lastMessage) {
        // in this case, we haven't started getting a response yet, so let's just retry the send
        console.log('Retrying message on connection close')
        const messageToResend = lastMessage
        setLastMessage(null);
        sendMessage(messageToResend);
      }
      setConnectionClosed(false);
    };

    if (connectionClosed) {
      onConnectionClosed(chats);
    }
  }, [connectionClosed, sendMessage, chats, lastMessage]);

  const renderChat = (chat, index) => {
    return chat.position === 'left' ? (
      <Grid container style={style.messageItem} key={index} justifyContent="flex-start">
        <Grid item xs={12}>
          <ChatItem
            loading={chat.loading}
            id={chat.id}
            position={chat.position}
            content={chat.content}
            sources={chat.sources}
            footer={chat.footer}
            salt={salt}
            uuid={uuid}
            user={'AL'}
            color={chat.error ? '#BA1A1A' : (responseIconUrl ? 'transparent' : '#4F616E')}
            background={chat.error ? '#FFDAD6' : '#DDE3EA'}
            showActions={!chat.internal}
            date={chat.date}
            cancel={chat.cancel}
            error={chat.error}
            logger={logger}
            anonGroup={null}
            remFontSize={1.0}
            customResponseImageUrl={responseIconUrl}
          />
        </Grid>
      </Grid>
    ) : (
      <Grid container style={style.messageItem} key={index} justifyContent="flex-end">
        <Grid item xs={12} flex>
          <ChatItem
            loading={chat.loading}
            position={chat.position}
            content={chat.content}
            background="#F9F9FC"
            date={chat.date}
            error={chat.error}
            logger={logger}
            anonGroup={null}
            remFontSize={1.0}
            customResponseImageUrl={responseIconUrl}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <ConsentDialog
        open={openConsent}
        handleClose={() => setOpenConsent(false)}
        user={user}
        terms={informedConsentTerms}
        chatTitle={chatTitle}
        setChatInputFocus={setChatInputFocus}
        messageRef={messageRef}
      />
      <ErrorDialog
        open={errorAlert}
        handleClose={() => setErrorAlert(false)}
        message={errorMessage}
      />
      <Grid container spacing={0} alignItems="center" style={style.chat}>
        <Grid item xs={1} sm={1} md={1} lg={2} style={{ display: desktopMode ? 'block' : 'none' }}>
          <LeftMenu
            view={view}
            setView={setView}
            afterMenuChange={() => {
              setChatInputFocus(messageRef);
              setTimeout(beginAutoScroll, 100);
            }}
            surveyToolsAllowed={surveyToolsAllowed}
          />
        </Grid>
        <Grid item xs={11} sm={11} md={11} lg={10}>
          <Container maxWidth="lg">
            <Box
              ref={view === 'chat' ? messagesEndRef : null}
              style={{
                ...style.messageBox,
                height: 'calc(100vh - 64px - ' + (numberOfInputRows - 1) * 23 + 'px - 1rem)',
              }}
            >
              {view === 'settings' ? (
                <SettingsSection
                  user={user}
                  signOut={signOut}
                  desktopMode={desktopMode}
                  versionText={versionText}
                  aboutText={aboutText}
                />
              ) : view === 'survey' ? (
                <SurveySection
                  logger={logger}
                  desktopMode={desktopMode}
                  versionText={versionText}
                  location={chatLocation}
                  anonGroup={null}
                  noTitle={false}
                  noExpressions={false}
                  noReviews={false}
                />
              ) : (
                <Grid id="chat-log" container spacing={0} alignItems="center">
                  <div
                    style={{
                      flexDirection: 'column',
                      alignItems: 'start',
                      width: '100%',
                    }}
                  >
                    <h2 style={style.textTitle}>{chatTitle}</h2>
                    <p style={style.textSubtitle}>
                      <a target="_blank" rel="noreferrer" href="/terms-and-conditions">
                        {versionText}
                      </a>
                    </p>
                    <hr style={{ color: '#C1C7CE' }} />
                  </div>
                  {chats.map((chat, index) => renderChat(chat, index))}
                </Grid>
              )}
            </Box>
          </Container>
        </Grid>
      </Grid>
      {view === 'chat' && (
        <div
          style={{
            ...style.chatContainer,
            height: 'calc(80px + ' + (numberOfInputRows - 1) * 23 + 'px)',
          }}
        >
          <Container
            maxWidth="md"
            style={{
              display: 'flex',
              height: '100%',
              alignItems: 'center',
              paddingLeft: 0,
              paddingRight: 0,
            }}
          >
            <Grid container spacing={0} alignItems="center">
              <Grid item xs={2} sm={0} style={{ display: desktopMode ? 'none' : 'block' }}>
                <LeftMenuMobile view={view} setView={setView} surveyToolsAllowed={surveyToolsAllowed} />
              </Grid>

              <Grid id="chat-input" item xs={10} sm={12} style={{ paddingTop: 15 }}>
                <ChatInput
                  messageRef={messageRef}
                  onMessageSubmitted={onMessageSent}
                  disabled={workingUI}
                  numberOfInputRows={numberOfInputRows}
                  setNumberOfInputRows={setNumberOfInputRows}
                  desktopMode={desktopMode}
                  setCancel={setCancel}
                  setChatInputFocus={setChatInputFocus}
                  logger={logger}
                  onResetChat={onResetChat}
                  chats={chats}
                  supportXLSForm={surveyToolsAllowed}
                  useXLSForm={useXLSForm}
                />
              </Grid>
            </Grid>
          </Container>
        </div>
      )}
    </>
  );
};

export default ChatSection;
